import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Building2, School, Heart, Users, Globe, Factory, ArrowRight, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

const industries = [
  {
    icon: Building2,
    name: 'Government Departments',
    projects: '100+',
    description: 'Trusted partner for government infrastructure projects across Maharashtra and India.',
    services: ['Smart Classrooms', 'Office Furniture', 'Security Systems', 'Digital Solutions'],
    stats: { clients: '50+', districts: '25+', tenders: '75+' },
  },
  {
    icon: School,
    name: 'Schools & Colleges',
    projects: '200+',
    description: 'Complete educational infrastructure solutions from furniture to smart classrooms.',
    services: ['School Furniture', 'Smart Education', 'Laboratory Setup', 'Library Solutions'],
    stats: { institutions: '200+', students: '1L+', classrooms: '500+' },
  },
  {
    icon: Heart,
    name: 'Healthcare',
    projects: '50+',
    description: 'Healthcare infrastructure and medical equipment for hospitals and medical colleges.',
    services: ['Hospital Furniture', 'Medical Equipment', 'Central Kitchen', 'Nutrition Programs'],
    stats: { hospitals: '50+', beds: '5K+', kitchens: '20+' },
  },
  {
    icon: Users,
    name: 'Corporate',
    projects: '150+',
    description: 'Modern office solutions for corporate clients across industries.',
    services: ['Office Furniture', 'IT Infrastructure', 'Security Systems', 'Digital Solutions'],
    stats: { companies: '150+', employees: '50K+', offices: '200+' },
  },
  {
    icon: Globe,
    name: 'Smart Cities',
    projects: '20+',
    description: 'Smart city infrastructure including surveillance, digital solutions, and more.',
    services: ['City Surveillance', 'Digital Platforms', 'Citizen Services', 'IoT Solutions'],
    stats: { cities: '20+', cameras: '1000+', projects: '30+' },
  },
  {
    icon: Factory,
    name: 'Manufacturing',
    projects: '75+',
    description: 'Industrial infrastructure and safety solutions for manufacturing units.',
    services: ['Industrial Furniture', 'Safety Systems', 'Cafeteria Setup', 'Security'],
    stats: { factories: '75+', units: '100+', workers: '25K+' },
  },
]

export default function Industries() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary-900/30 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Globe className="w-4 h-4 text-secondary-400" />
              <span className="text-sm text-graphite-300">Industries We Serve</span>
            </div>
            <h1 className="heading-xl text-white mb-6">
              Trusted Across <span className="gradient-text-cyan">Industries</span>
            </h1>
            <p className="text-xl text-graphite-300">
              From government departments to smart cities, we deliver specialized solutions
              for diverse institutional requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="hover" className="h-full p-6 group">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
                      <industry.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white group-hover:text-primary-400 transition-colors">
                        {industry.name}
                      </h3>
                      <p className="text-primary-400 text-sm">{industry.projects} Projects</p>
                    </div>
                  </div>

                  <p className="text-graphite-400 mb-4">{industry.description}</p>

                  <div className="space-y-2 mb-4">
                    {industry.services.map((service, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-graphite-300">
                        <CheckCircle2 className="w-4 h-4 text-secondary-400" />
                        <span>{service}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {Object.entries(industry.stats).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-primary-400 font-bold">{value}</div>
                          <div className="text-xs text-graphite-500 capitalize">{key}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-premium">
          <Card variant="premium" className="p-12 text-center">
            <h2 className="heading-md text-white mb-4">Your Industry Not Listed?</h2>
            <p className="text-graphite-400 mb-8 max-w-2xl mx-auto">
              We work across multiple industries. Contact us to discuss your specific requirements.
            </p>
            <Button variant="gold" size="lg" asChild>
              <Link to="/contact">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </Card>
        </div>
      </section>
    </div>
  )
}